import { FrameElement, IllustrationElement, ImageElement, LayoutElementType, PapyrusComponent } from "../types";
import { degreesToRadians } from "../util/math";
import { modifySVGColors } from "../util/illustration";
import { svgToDataUrl } from "../util/crossplatform";
import { Group } from "./Group";
import { Fragment } from "preact/jsx-runtime";

export const Frame: PapyrusComponent<Omit<FrameElement, "type">> = (props) => {
    const patternLinkId = `spiff-frame-${props.id}`;
    const thresholdFilterId = `spiff-frame-threshold-${props.id}`;

    const getFrameContentSrc = () => {
        if (!props.pattern) {
            return "";
        }
        if (props.pattern.svg) {
            const modifiedSvg = modifySVGColors(props.pattern.svg, props.pattern.colors || {}, false);
            return svgToDataUrl(modifiedSvg);
        }
        return props.pattern.src;
    };

    if (!getFrameContentSrc()) {
        return <Fragment />;
    }

    const thresholdTableValues = (): string => {
        if (!props.threshold) {
            return "";
        }
        if (props.invertThreshold) {
            return "1 ".repeat(props.threshold) + "0 ".repeat(256 - props.threshold);
        }
        return "0 ".repeat(props.threshold) + "1 ".repeat(256 - props.threshold);
    };

    const renderDefinitions = () => {
        return (
            <>
                {props.useThreshold && (
                    <defs>
                        <filter id={thresholdFilterId} color-interpolation-filters="sRGB">
                            <feColorMatrix
                                type="matrix"
                                values={`
                            ${props.thresholdSaturation} ${props.thresholdSaturation} ${props.thresholdSaturation} 0 0
                            ${props.thresholdSaturation} ${props.thresholdSaturation} ${props.thresholdSaturation} 0 0
                            ${props.thresholdSaturation} ${props.thresholdSaturation} ${props.thresholdSaturation} 0 0
                            0 0 0 1 0
                        `}
                                result="greyscale"
                            ></feColorMatrix>
                            <feComponentTransfer in="greyscale">
                                <feFuncR type="discrete" tableValues={thresholdTableValues()} />
                                <feFuncG type="discrete" tableValues={thresholdTableValues()} />
                                <feFuncB type="discrete" tableValues={thresholdTableValues()} />
                            </feComponentTransfer>
                        </filter>
                    </defs>
                )}
            </>
        );
    };

    // We only want to blur when a pattern is provided.
    const rotation = props.rotation || 0;
    const angleInRadians = degreesToRadians(rotation);
    const rotC = Math.cos(angleInRadians);
    const rotS = -Math.sin(angleInRadians);

    const getFrameContent = () => {
        const patternX = props.pattern?.x || 0;
        const patternY = props.pattern?.y || 0;
        const patternWidth = props.pattern?.width || 0;
        const patternHeight = props.pattern?.height || 0;
        const patternScaleX = props.pattern?.scaleX || 1;
        const patternScaleY = props.pattern?.scaleY || 1;
        const patternRotation = props.pattern?.rotation || 0;

        const children = props.pattern?.svg
            ? ({
                  id: `${patternLinkId}-contents`,
                  type: LayoutElementType.Illustration,
                  x: patternX,
                  y: patternY,
                  rotation: patternRotation,
                  width: patternWidth * patternScaleX,
                  height: patternHeight * patternScaleY,
                  src: getFrameContentSrc(),
                  svg: props.pattern?.svg,
                  colors: props.pattern?.colors,
              } as IllustrationElement)
            : ({
                  id: `${patternLinkId}-contents`,
                  type: LayoutElementType.Image,
                  x: patternX,
                  y: patternY,
                  rotation: patternRotation,
                  width: patternWidth * patternScaleX,
                  height: patternHeight * patternScaleY,
                  src: getFrameContentSrc(),
              } as ImageElement);
        return [children];
    };

    return (
        <>
            {renderDefinitions()}
            <g
                opacity={props.opacity}
                mask={props.stepName ? undefined : props.mask}
                filter={props.useThreshold ? `url(#${thresholdFilterId})` : undefined}
            >
                <g
                    transform={`
                matrix(1, 0, 0, 1, ${props.x}, ${props.y})
                matrix(1, 0, 0, 1, ${props.width / 2}, ${props.height / 2})
                matrix(${rotC}, ${-rotS}, ${rotS}, ${rotC}, 0, 0)
                matrix(1, 0, 0, 1, ${-props.width / 2}, ${-props.height / 2})
                matrix(${props.scaleX}, 0, 0, ${props.scaleY}, 0, 0)
                `}
                >
                    <Group
                        id={patternLinkId}
                        x={0}
                        y={0}
                        rotation={0}
                        width={props.width}
                        height={props.height}
                        clipPath={props.path}
                        children={getFrameContent()}
                    />
                </g>
            </g>
        </>
    );
};
